
import {
  displayValue,
  formatOrderTime,
  getOrderDeliveryNote,
  getSoftwareCode,
  getStatusBadge,
  getOrderFailureReason,
  isClosedOrder,
  isCompletedOrder,
  isCouponOrder,
  isPendingReceive,
  isPendingShip,
  isPhysicalOrder,
  isSoftwareOrder,
  isApiSuccess,
} from "@/utils/mallExchange";
import { openMallCouponSite, resolveSelectedMallSiteDomain } from "@/utils/mallSite";

export default {
  name: "XcoinsExchangeDetailDialog",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    orderId: {
      type: [String, Number],
      default: "",
    },
    focus: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      detail: {},
      loading: false,
      loadError: false,
      confirming: false,
      copying: false,
      openingCoupon: false,
      couponSiteUrl: "", // 当前选中国家的站点域名
      fetchQueued: false, // 同一次打开 visible 和 orderId 会一起变，合并成一次请求
    };
  },
  computed: {
    goodsTypeLabel() {
      if (isSoftwareOrder(this.detail)) {
        return this.$t("xcoins.GOODS_TYPE_SOFTWARE");
      }
      if (isPhysicalOrder(this.detail)) {
        return this.$t("xcoins.GOODS_TYPE_PHYSICAL");
      }
      if (isCouponOrder(this.detail)) {
        return this.$t("xcoins.GOODS_TYPE_COUPON");
      }
      return "";
    },
    statusBadge() {
      return getStatusBadge(this.detail);
    },
    isPhysical() {
      return isPhysicalOrder(this.detail);
    },
    isSoftware() {
      return isSoftwareOrder(this.detail);
    },
    isCoupon() {
      return isCouponOrder(this.detail);
    },
    isClosed() {
      return isClosedOrder(this.detail);
    },
    closedFailureReason() {
      return getOrderFailureReason(this.detail);
    },
    isCompleted() {
      return isCompletedOrder(this.detail);
    },
    softwareCode() {
      return getSoftwareCode(this.detail);
    },
    firstCardLabel() {
      return this.isPhysical ? this.$t("xcoins.SPECS") : this.$t("xcoins.REDEEM_QTY");
    },
    firstCardValue() {
      if (this.isPhysical) {
        return this.detail.detailName || "---";
      }
      return this.detail.quantity || 1;
    },
    deliveryNoteText() {
      return getOrderDeliveryNote(this.detail);
    },
    receiverLine() {
      const name = this.detail.receiverName || "";
      const mobile = this.detail.receiverMobile || "";
      return [name, mobile].filter(Boolean).join("  ") || "--";
    },
    showLogistics() {
      return Boolean(this.detail.logisticsCompany || this.detail.trackingNo);
    },
    logisticsText() {
      return [this.detail.logisticsCompany, this.detail.trackingNo].filter(Boolean).join(" · ") || "--";
    },
    physicalBlockTitle() {
      return this.isCompleted
        ? this.$t("xcoins.SHIPPING_AND_LOGISTICS")
        : this.$t("xcoins.SHIPPING_AND_DELIVERY");
    },
    physicalTip() {
      if (isPendingShip(this.detail) && !this.deliveryNoteText) {
        return this.$t("xcoins.SHIP_PENDING_TIP");
      }
      if (isPendingReceive(this.detail)) {
        return this.$t("xcoins.RECEIVE_TIP");
      }
      return "";
    },
    receiveTypeText() {
      if (Number(this.detail.receiveType) === 2) {
        return this.$t("xcoins.RECEIVE_BY_SYSTEM");
      }
      if (Number(this.detail.receiveType) === 1) {
        return this.$t("xcoins.RECEIVE_BY_USER");
      }
      return "--";
    },
    refundText() {
      if (this.detail.totalPoints || this.detail.refundNo) {
        return this.$t("xcoins.POINTS_REFUNDED", { n: this.detail.totalPoints || "--" });
      }
      return "--";
    },
    footerActions() {
      if (this.isSoftware && this.softwareCode && !this.isClosed) {
        return [{ key: "copyCode", labelKey: "xcoins.COPY_SOFTWARE_CODE", primary: true }];
      }
      if (this.isCoupon && !this.isClosed && this.couponSiteUrl) {
        return [{ key: "useCoupon", labelKey: "xcoins.USE_COUPON", primary: true }];
      }
      if (isPendingReceive(this.detail)) {
        const actions = [];
        if (this.detail.trackingNo) {
          actions.push({ key: "copyTracking", labelKey: "xcoins.COPY_TRACKING_NO", primary: false });
        }
        actions.push({ key: "confirm", labelKey: "xcoins.CONFIRM_RECEIPT", primary: true });
        return actions;
      }
      return [];
    },
  },
  watch: {
    visible(value) {
      if (value) {
        this.queueFetchDetail();
      } else {
        this.detail = {};
        this.loadError = false;
      }
    },
    orderId() {
      if (this.visible) {
        this.queueFetchDetail();
      }
    },
  },
  methods: {
    displayValue,
    formatOrderTime,
    close() {
      this.$emit("close");
    },
    async copyText(text, successKey = "page.COPY_SUCCESS") {
      if (!text || this.copying) {
        return;
      }
      this.copying = true;
      try {
        await this.$copyText(text);
        this.$message.success(this.$t(successKey));
      } catch (e) {
        this.$message.error(this.$t("xcoins.COPY_MANUAL"));
      } finally {
        this.copying = false;
      }
    },
    handleAction(key) {
      if (key === "copyCode") {
        this.copyText(this.softwareCode);
        return;
      }
      if (key === "copyTracking") {
        this.copyText(this.detail.trackingNo);
        return;
      }
      if (key === "useCoupon") {
        this.openCouponSite();
        return;
      }
      if (key === "confirm") {
        this.$emit("request-confirm", this.detail.id);
      }
    },
    /** 按当前选中国家打开商城站点 */
    async openCouponSite() {
      if (this.openingCoupon) {
        return;
      }
      this.openingCoupon = true;
      try {
        const url = this.couponSiteUrl || await resolveSelectedMallSiteDomain(this.$global, this.$cookies);
        this.couponSiteUrl = url;
        if (!url) {
          return;
        }
        openMallCouponSite(url);
      } finally {
        this.openingCoupon = false;
      }
    },
    /** 同一 tick 里 visible / orderId 都变时只打一次详情 */
    queueFetchDetail() {
      if (this.fetchQueued) {
        return;
      }
      this.fetchQueued = true;
      this.$nextTick(() => {
        this.fetchQueued = false;
        if (this.visible && this.orderId) {
          this.fetchDetail();
        }
      });
    },
    /** 按订单 ID 拉取兑换详情快照 */
    async fetchDetail() {
      if (!this.orderId) {
        return;
      }
      this.loading = true;
      this.loadError = false;
      try {
        const res = await this.$pointsCache.requestWithPointToken((token) => {
          return this.$global.pointsMallOrderDetail(this.orderId, token);
        });
        if (isApiSuccess(res) && res.data) {
          this.detail = res.data;
          if (isCouponOrder(this.detail) && !isClosedOrder(this.detail)) {
            this.couponSiteUrl = await resolveSelectedMallSiteDomain(this.$global, this.$cookies);
          } else {
            this.couponSiteUrl = "";
          }
          return;
        }
        this.loadError = true;
      } catch (e) {
        this.loadError = true;
      } finally {
        this.loading = false;
      }
    },
  },
};
